import { MiniTopic } from '@deepguide-ai/types'

export interface DataResolverConfig {
  /**
   * Function to resolve topic data from context when available
   */
  contextResolver?: () => MiniTopic[]
  /**
   * Function to resolve topic data from API when needed
   */
  apiResolver?: (topicIds: string[]) => Promise<MiniTopic[]>
  /**
   * Function to resolve step data from context when available
   */
  stepContextResolver?: (topicId: string) => any[] // Step data from context
  /**
   * Function to resolve step data from API when needed
   */
  stepsByTopicIdApiResolver?: (topicId: string) => Promise<any[]>
  /**
   * Function to resolve a single step by ID directly from API
   */
  stepByIdApiResolver?: (stepId: string) => Promise<any | null>
  /**
   * Function to resolve workspace data from context
   */
  workspaceResolver?: () => { id: string; slug: string; title: string } | null
}

export interface DataResolverOptions {
  /**
   * Topic IDs to resolve
   */
  topicIds?: string[]
  /**
   * Whether to include fallback data if live data is unavailable
   */
  includeFallback?: boolean
  /**
   * Serialized topic data to use as fallback
   */
  fallbackData?: MiniTopic[]
  /**
   * Selected topic IDs to filter by
   */
  selectedItems?: string[]
  /**
   * Custom order for topic IDs
   */
  customOrder?: string[]
}

/**
 * Data resolver service that can resolve topic data from multiple sources
 * with fallback support
 */
export class DataResolver {
  private config: DataResolverConfig

  constructor(config: DataResolverConfig = {}) {
    this.config = config
  }

  /**
   * Update the resolver configuration
   */
  updateConfig(config: Partial<DataResolverConfig>) {
    this.config = { ...this.config, ...config }
  }

  /**
   * Get the current workspace from context
   */
  getWorkspace(): { id: string; slug: string; title: string } | null {
    if (this.config.workspaceResolver) {
      return this.config.workspaceResolver()
    }
    return null
  }

  /**
   * Resolve topic data based on options
   */
  async resolve(options: DataResolverOptions): Promise<MiniTopic[]> {
    let resolvedData: MiniTopic[] = []

    // Try context resolver first (this should always work with Jotai data)
    if (this.config.contextResolver) {
      try {
        const contextData = this.config.contextResolver()
        if (contextData && contextData.length > 0) {
          // If we have a single topic ID, find its direct children
          if (options.topicIds && options.topicIds.length === 1) {
            const parentTopicId = options.topicIds[0]
            
            // Find all direct children of the parent topic
            const directChildren = contextData.filter(topic => topic.parentId === parentTopicId)
            
            console.log('📁 [DataResolver] Finding children for parent:', parentTopicId, 'Found:', directChildren.length, 'from context size:', contextData.length)
            
            if (directChildren.length > 0) {
              resolvedData = directChildren
            } else {
              // Fallback: try to find parent topic and use its subtopics property if available
              const parentTopic = contextData.find(t => t.id === parentTopicId)
              if (parentTopic && parentTopic.subtopics) {
                console.log('📁 [DataResolver] Using subtopics from parent topic:', parentTopic.subtopics.length)
                resolvedData = parentTopic.subtopics
              }
            }
          } else {
            // Otherwise use all context data
            resolvedData = contextData
          }
        }
      } catch (error) {
        console.warn('Context resolver failed:', error)
      }
    }

    // Try API resolver if context failed and we have specific topic IDs
    if (resolvedData.length === 0 && this.config.apiResolver && options.topicIds) {
      try {
        resolvedData = await this.config.apiResolver(options.topicIds)
      } catch (error) {
        console.warn('API resolver failed:', error)
      }
    }

    // Use fallback data if available
    if (resolvedData.length === 0 && options.includeFallback && options.fallbackData) {
      resolvedData = options.fallbackData
    }

    return this.processTopics(resolvedData, options)
  }

  /**
   * Process topics based on selection and ordering options
   */
  private processTopics(topics: MiniTopic[], options: DataResolverOptions): MiniTopic[] {
    let result = [...topics]

    // Filter by selected items if specified
    if (options.selectedItems && options.selectedItems.length > 0) {
      result = result.filter(topic => options.selectedItems!.includes(topic.id))
    }

    // Apply custom ordering if specified
    if (options.customOrder && options.customOrder.length > 0) {
      const orderMap = Object.fromEntries(
        options.customOrder.map((id, index) => [id, index])
      )
      result.sort((a, b) => {
        const aOrder = orderMap[a.id] ?? Number.MAX_SAFE_INTEGER
        const bOrder = orderMap[b.id] ?? Number.MAX_SAFE_INTEGER
        return aOrder - bOrder
      })
    }

    return result
  }

  /**
   * Resolve step data globally by stepId only
   */
  async resolveStepGlobal(stepId: string): Promise<any | null> {
    let step: any = null

    // Try to search through all topics' steps if we have a context resolver
    if (this.config.contextResolver && this.config.stepContextResolver) {
      try {
        const allTopics = this.config.contextResolver()
        console.log("All topics", allTopics);
        // Search through all topics to find the step
        for (const topic of allTopics) {
          try {
            const steps = this.config.stepContextResolver(topic.id)
            const foundStep = steps.find(s => s.id === stepId)
            if (foundStep) {
              step = foundStep
              console.log('📷 [DataResolver] Step found globally:', {
                stepId,
                foundInTopic: topic.id,
                topicTitle: topic.title
              })
              break
            }
          } catch (_error) {
            // Continue searching in other topics
            continue
          }
        }
      } catch (error) {
        console.warn('Global step resolution failed:', error)
      }
    }

    return step
  }

  /**
   * Resolve step data for a specific topic and step ID
   */
  async resolveStep(topicId: string, stepId: string): Promise<any | null> {
    let step: any = null

    // Try step context resolver first
    if (this.config.stepContextResolver) {
      try {
        const steps = this.config.stepContextResolver(topicId)
        step = steps.find(s => s.id === stepId) || null
        
        console.log('📷 [DataResolver] Step context resolution:', {
          topicId,
          stepId,
          stepsAvailable: steps.length,
          stepFound: !!step
        })
      } catch (error) {
        console.warn('Step context resolver failed:', error)
      }
    }

    // Try step API resolver if context failed
    if (!step && this.config.stepsByTopicIdApiResolver) {
      try {
        const steps = await this.config.stepsByTopicIdApiResolver(topicId)
        step = steps.find(s => s.id === stepId) || null
        
        console.log('📷 [DataResolver] Step API resolution:', {
          topicId,
          stepId,
          stepsAvailable: steps.length,
          stepFound: !!step
        })
      } catch (error) {
        console.warn('Step API resolver failed:', error)
      }
    }

    return step
  }

  /**
   * Resolve a single step by ID directly (most efficient for single step lookups)
   */
  async resolveStepById(stepId: string): Promise<any | null> {
    let step: any = null

    // Try direct step-by-ID API resolver first (most efficient)
    if (this.config.stepByIdApiResolver && stepId) {
      try {
        step = await this.config.stepByIdApiResolver(stepId)
        
        console.log('📷 [DataResolver] Direct step-by-ID resolution:', {
          stepId,
          stepFound: !!step,
          method: 'stepByIdApiResolver'
        })
        
        if (step) {
          return step
        }
      } catch (error) {
        console.warn('Step-by-ID API resolver failed:', error)
      }
    }
    else {
      console.warn("No stepByIdApiResolver found");
    }

    // Fallback to global resolution if direct resolver not available or failed
    return this.resolveStepGlobal(stepId)
  }
}

// Global instance for the data resolver
let globalDataResolver: DataResolver | null = null

/**
 * Get or create the global data resolver instance
 */
export function getDataResolver(config?: DataResolverConfig): DataResolver {
  if (!globalDataResolver) {
    globalDataResolver = new DataResolver(config)
  } else if (config) {
    globalDataResolver.updateConfig(config)
  }
  return globalDataResolver
}

/**
 * Initialize the data resolver with configuration
 */
export function initializeDataResolver(config: DataResolverConfig): DataResolver {
  globalDataResolver = new DataResolver(config)
  return globalDataResolver
}